import { ApolloError } from "@apollo/client";
import { Alert, styled, Typography } from "@mui/material";
import _ from "lodash";
import { ReactElement, ReactNode, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import {
  AnonymousOptOutType,
  SubaccountOptOutNumberDetailsQuery,
  TenantOptOutNumberDetailsQuery,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import { useCurrentUserHasTenantWideAccess } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";

import { AccentBox } from "../AccentBox";
import ErrorCard from "../ErrorCard";
import FormattedDateTime from "../FormattedDateTime";
import { InternalLink } from "../Link";
import { TwoColumnDisplay, TwoColumnDisplayRow } from "../TwoColumnDisplay";

const NoBulletList = styled("ul")({
  listStyle: "none",
  paddingLeft: 0,
  margin: 0,
});

type OptOutNumberDetails =
  | SubaccountOptOutNumberDetailsQuery["subaccountOptOutNumberDetails"]
  | TenantOptOutNumberDetailsQuery["tenantOptOutNumberDetails"];

type MaybeAnonymousOptOut = OptOutNumberDetails["optOuts"][number];

type OptOut = Extract<MaybeAnonymousOptOut, { __typename?: "OptOut" }>;
type OptOutWithSourceType<T> = OptOut & {
  source: Extract<OptOut["source"], { __typename?: T }>;
};
type AdminListOptOut = OptOutWithSourceType<"OptOutAdminListSource">;
type CampaignAutomaticOptOut =
  OptOutWithSourceType<"OptOutCampaignAutomaticSource">;
type CampaignManualOptOut = OptOutWithSourceType<"OptOutCampaignManualSource">;
type ShortCodeKeywordOptOut =
  OptOutWithSourceType<"OptOutShortCodeKeywordSource">;
type SpokeOptOut = OptOutWithSourceType<"OptOutSpokeSource">;

type AnonymousOptOut = Extract<
  MaybeAnonymousOptOut,
  { __typename?: "AnonymousOptOut" }
>;

type MaybeAnonymousTemporaryOptOut =
  OptOutNumberDetails["temporaryOptOuts"][number];

type TemporaryOptOut = Extract<
  MaybeAnonymousTemporaryOptOut,
  { __typename?: "TemporaryOptOut" }
>;
type AnonymousTemporaryOptOut = Extract<
  MaybeAnonymousTemporaryOptOut,
  { __typename?: "AnonymousTemporaryOptOut" }
>;

export default function OptedOutNumberDetail({
  phoneNumber,
  optOutDetails,
  optOutDetailsLoading,
  optOutDetailsError,
}: {
  phoneNumber: string;
  optOutDetails?: OptOutNumberDetails | null;
  optOutDetailsLoading: boolean;
  optOutDetailsError?: ApolloError | null;
}): ReactElement {
  const subaccountContext = useSubaccount();
  const userHasTenantWideAccess = useCurrentUserHasTenantWideAccess();

  const hasLegallyRequiredOptOuts = optOutDetails?.optOuts.some(
    optOut =>
      optOut.__typename === "OptOut" &&
      (optOut.source.__typename === "OptOutCampaignAutomaticSource" ||
        optOut.source.__typename === "OptOutShortCodeKeywordSource") &&
      !optOut.deletedAt
  );

  const globalOptOutListRow: TwoColumnDisplayRow | null = useMemo(() => {
    const globalAdminListOptOuts = optOutDetails?.optOuts.filter(
      (optOut): optOut is AdminListOptOut =>
        optOut.__typename === "OptOut" &&
        optOut.source.__typename === "OptOutAdminListSource" &&
        !optOut.subaccount
    );
    if (globalAdminListOptOuts?.length) {
      return {
        key: "global-opt-out-lists",
        label: (
          <FormattedMessage
            defaultMessage="Global Opt Out Lists"
            description="Label for global list Opt Outs"
          />
        ),
        content: (
          <NoBulletList>
            {globalAdminListOptOuts?.map(optOut => (
              <OptOutItem
                id={optOut.id}
                title={
                  optOut.source.list?.name ?? (
                    <FormattedMessage
                      defaultMessage="Unknown list"
                      description="Label for an unknown Opt Out list"
                    />
                  )
                }
                creationLabel={
                  <ListOptOutCreationLabel
                    createdBy={optOut.source.list?.createdByUser?.fullName}
                    createdAt={optOut.createdAt}
                  />
                }
                deletedAt={optOut.deletedAt}
              />
            ))}
          </NoBulletList>
        ),
      };
    } else {
      return null;
    }
  }, [optOutDetails]);

  const subaccountOptOutListRow: TwoColumnDisplayRow | null = useMemo(() => {
    const subaccountAdminListOptOuts = optOutDetails?.optOuts.filter(
      (optOut): optOut is AdminListOptOut =>
        optOut.__typename === "OptOut" &&
        optOut.source.__typename === "OptOutAdminListSource" &&
        !!optOut.subaccount
    );
    if (subaccountAdminListOptOuts?.length) {
      return {
        key: "subaccount-opt-out-lists",
        label: subaccountContext ? (
          <FormattedMessage
            defaultMessage="Opt Out Lists"
            description="Label for list Opt Outs"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Subaccount Opt Out Lists"
            description="Label for subaccount list Opt Outs"
          />
        ),
        content: (
          <NoBulletList>
            {subaccountAdminListOptOuts?.map(optOut => (
              <OptOutItem
                id={optOut.id}
                title={
                  optOut.source.list?.name ?? (
                    <FormattedMessage
                      defaultMessage="Unknown list"
                      description="Label for an unknown Opt Out list"
                    />
                  )
                }
                subaccount={optOut.subaccount}
                creationLabel={
                  <ListOptOutCreationLabel
                    createdBy={optOut.source.list?.createdByUser?.fullName}
                    createdAt={optOut.createdAt}
                  />
                }
                deletedAt={optOut.deletedAt}
              />
            ))}
          </NoBulletList>
        ),
      };
    } else {
      return null;
    }
  }, [optOutDetails, subaccountContext]);

  const globalStrikeListRow: TwoColumnDisplayRow | null = useMemo(() => {
    const globalStrikeListOptOuts = optOutDetails?.temporaryOptOuts.filter(
      (temporaryOptOut): temporaryOptOut is TemporaryOptOut =>
        temporaryOptOut.__typename === "TemporaryOptOut" &&
        temporaryOptOut.source.__typename ===
          "TemporaryOptOutAdminListSource" &&
        !temporaryOptOut.subaccount
    );
    if (globalStrikeListOptOuts?.length) {
      return {
        key: "global-strike-lists",
        label: "Global Strike Lists",
        content: (
          <NoBulletList>
            {globalStrikeListOptOuts.map(optOut => (
              <OptOutItem
                id={optOut.id}
                title={
                  optOut.source.list?.name ?? (
                    <FormattedMessage
                      defaultMessage="Unknown list"
                      description="Label for an unknown Opt Out list"
                    />
                  )
                }
                creationLabel={
                  <ListOptOutCreationLabel
                    createdBy={optOut.source.list?.createdByUser?.fullName}
                    createdAt={optOut.createdAt}
                  />
                }
                expiresAt={optOut.expiresAt}
                deletedAt={optOut.deletedAt}
              />
            ))}
          </NoBulletList>
        ),
      };
    } else {
      return null;
    }
  }, [optOutDetails]);

  const subaccountStrikeListRow: TwoColumnDisplayRow | null = useMemo(() => {
    const strikeListOptOuts = optOutDetails?.temporaryOptOuts.filter(
      (temporaryOptOut): temporaryOptOut is TemporaryOptOut =>
        temporaryOptOut.__typename === "TemporaryOptOut" &&
        temporaryOptOut.source.__typename ===
          "TemporaryOptOutAdminListSource" &&
        !!temporaryOptOut.subaccount
    );
    if (strikeListOptOuts?.length) {
      return {
        key: "subaccount-strike-lists",
        label: subaccountContext ? (
          <FormattedMessage
            defaultMessage="Strike Lists"
            description="Label for strike lists"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Subaccount Strike Lists"
            description="Label for subaccount strike lists"
          />
        ),
        content: (
          <NoBulletList>
            {strikeListOptOuts.map(optOut => (
              <OptOutItem
                id={optOut.id}
                title={
                  optOut.source.list?.name ?? (
                    <FormattedMessage
                      defaultMessage="Unknown list"
                      description="Label for an unknown Opt Out list"
                    />
                  )
                }
                subaccount={optOut.subaccount}
                creationLabel={
                  <ListOptOutCreationLabel
                    createdBy={optOut.source.list?.createdByUser?.fullName}
                    createdAt={optOut.createdAt}
                  />
                }
                expiresAt={optOut.expiresAt}
                deletedAt={optOut.deletedAt}
              />
            ))}
          </NoBulletList>
        ),
      };
    } else {
      return null;
    }
  }, [optOutDetails, subaccountContext]);

  const autoOptOutRow: TwoColumnDisplayRow | null = useMemo(() => {
    const autoOptOuts = optOutDetails?.optOuts.filter(
      (optOut): optOut is CampaignAutomaticOptOut =>
        optOut.__typename === "OptOut" &&
        optOut.source.__typename === "OptOutCampaignAutomaticSource"
    );
    if (autoOptOuts?.length) {
      return {
        key: "auto-opt-outs",
        label: (
          <FormattedMessage
            defaultMessage="Auto Opt Outs"
            description="Label for automatic Opt Outs"
          />
        ),
        content: (
          <NoBulletList>
            {autoOptOuts.map(optOut => (
              <OptOutItem
                id={optOut.id}
                title={
                  optOut.source.message?.campaignName ?? (
                    <FormattedMessage
                      defaultMessage="Unknown campaign"
                      description="Label for an unknown campaign"
                    />
                  )
                }
                subaccount={optOut.subaccount}
                creationLabel={
                  optOut.source.message ? (
                    <FormattedMessage
                      defaultMessage={'Texted "{optOutMessage}" on {createdAt}'}
                      description="Label for creation date of automatic Opt Out with known message text"
                      values={{
                        createdAt: (
                          <FormattedDateTime value={optOut.createdAt} />
                        ),
                        optOutMessage: optOut.source.message.text,
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage={"Texted stop phrase on {createdAt}"}
                      description="Label for creation date of automatic Opt Out with unknown message text"
                      values={{
                        createdAt: (
                          <FormattedDateTime value={optOut.createdAt} />
                        ),
                      }}
                    />
                  )
                }
                deletedAt={optOut.deletedAt}
              />
            ))}
          </NoBulletList>
        ),
      };
    } else {
      return null;
    }
  }, [optOutDetails]);

  const shortCodeOptOutRow: TwoColumnDisplayRow | null = useMemo(() => {
    const shortCodeOptOuts = optOutDetails?.optOuts.filter(
      (optOut): optOut is ShortCodeKeywordOptOut =>
        optOut.__typename === "OptOut" &&
        optOut.source.__typename === "OptOutShortCodeKeywordSource"
    );
    if (shortCodeOptOuts?.length) {
      return {
        key: "shortcode-opt-outs",
        label: (
          <FormattedMessage
            defaultMessage="Short Code Opt Outs"
            description="Label for short code Opt Outs"
          />
        ),
        content: (
          <NoBulletList>
            {shortCodeOptOuts.map(optOut => (
              <OptOutItem
                id={optOut.id}
                title={
                  optOut.source.ownedPhoneNumber?.phoneNumber ?? (
                    <FormattedMessage
                      defaultMessage="Unknown shortcode"
                      description="Label for an unknown shortcode"
                    />
                  )
                }
                creationLabel={
                  <FormattedMessage
                    defaultMessage="Texted Opt Out keyword on {createdAt}"
                    description="Label for creation date for a shortcode Opt Out"
                    values={{
                      createdAt: <FormattedDateTime value={optOut.createdAt} />,
                    }}
                  />
                }
                deletedAt={optOut.deletedAt}
              />
            ))}
          </NoBulletList>
        ),
      };
    } else {
      return null;
    }
  }, [optOutDetails]);

  const texterOptOutRow: TwoColumnDisplayRow | null = useMemo(() => {
    const texterOptOuts = optOutDetails?.optOuts.filter(
      (optOut): optOut is CampaignManualOptOut =>
        optOut.__typename === "OptOut" &&
        optOut.source.__typename === "OptOutCampaignManualSource"
    );
    if (texterOptOuts?.length) {
      return {
        key: "texter-opt-outs",
        label: (
          <FormattedMessage
            defaultMessage="Texter Opt Outs"
            description="Label for texter Opt Outs"
          />
        ),
        content: (
          <NoBulletList>
            {texterOptOuts.map(optOut => (
              <OptOutItem
                id={optOut.id}
                title={
                  optOut.source.campaign?.name ?? (
                    <FormattedMessage
                      defaultMessage="Unknown campaign"
                      description="Label for an unknown campaign"
                    />
                  )
                }
                creationLabel={
                  optOut.source.user ? (
                    <FormattedMessage
                      defaultMessage="Opted out by {createdBy} on {createdAt}"
                      description="Label for creation date for a manual Opt Out with a known creator"
                      values={{
                        createdAt: (
                          <FormattedDateTime value={optOut.createdAt} />
                        ),
                        createdBy: optOut.source.user.fullName,
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage="Opted out on {createdAt}"
                      description="Label for creation date for a manual Opt Out with an unknown creator"
                      values={{
                        createdAt: (
                          <FormattedDateTime value={optOut.createdAt} />
                        ),
                      }}
                    />
                  )
                }
                deletedAt={optOut.deletedAt}
              />
            ))}
          </NoBulletList>
        ),
      };
    } else {
      return null;
    }
  }, [optOutDetails]);

  const externalOptOutRow: TwoColumnDisplayRow | null = useMemo(() => {
    const externalOptOuts =
      optOutDetails?.optOuts.filter(
        (optOut): optOut is AnonymousOptOut | SpokeOptOut =>
          optOut.__typename === "AnonymousOptOut" ||
          (optOut.__typename === "OptOut" &&
            optOut.source.__typename === "OptOutSpokeSource")
      ) ?? [];
    const externalTemporaryOptOuts =
      optOutDetails?.temporaryOptOuts.filter(
        (temporaryOptOut): temporaryOptOut is AnonymousTemporaryOptOut =>
          temporaryOptOut.__typename === "AnonymousTemporaryOptOut"
      ) ?? [];
    const externalOptOutsAndTemporaryOptOuts = _.sortBy(
      [...externalOptOuts, ...externalTemporaryOptOuts],
      "createdAt"
    ).reverse();
    if (externalOptOutsAndTemporaryOptOuts.length) {
      return {
        key: "external-opt-outs",
        label: (
          <FormattedMessage
            defaultMessage="External Opt Outs"
            description="Label for external Opt Outs"
          />
        ),
        content: (
          <NoBulletList>
            {externalOptOutsAndTemporaryOptOuts.map(optOut => {
              let title: ReactNode;
              let note: ReactNode = null;
              if (
                optOut.__typename === "OptOut" &&
                optOut.source.__typename === "OptOutSpokeSource"
              ) {
                title = (
                  <FormattedMessage
                    defaultMessage="Opted Out in Spoke"
                    description="Label for a spoke Opt Out"
                  />
                );
              } else if (
                optOut.__typename === "AnonymousTemporaryOptOut" ||
                optOut.__typename === "AnonymousOptOut"
              ) {
                if (optOut.anonymousOptOutType === AnonymousOptOutType.Global) {
                  title = (
                    <FormattedMessage
                      defaultMessage="Global Opt Out"
                      description="Label for a global Opt Out"
                    />
                  );
                  note = userHasTenantWideAccess ? (
                    <InternalLink to="/organization/opt-outs">
                      <FormattedMessage
                        defaultMessage="Organization Opt Outs"
                        description="Link to the organization Opt Out page"
                      />
                    </InternalLink>
                  ) : (
                    <FormattedMessage
                      defaultMessage="Contact your organization owner for more details"
                      description="Message to contact the organization owner"
                    />
                  );
                } else {
                  title = (
                    <FormattedMessage
                      defaultMessage="Opted Out by Another Subaccount"
                      description="Label for an Opt Out from another subaccount "
                    />
                  );
                }
              } else {
                throw new Error("this should never happen");
              }
              return (
                <OptOutItem
                  id={optOut.id}
                  title={title}
                  creationLabel={
                    <FormattedMessage
                      defaultMessage="Added {createdAt}"
                      description="Label for creation date for an external Opt Out"
                      values={{
                        createdAt: (
                          <FormattedDateTime value={optOut.createdAt} />
                        ),
                      }}
                    />
                  }
                  expiresAt={
                    optOut.__typename === "AnonymousTemporaryOptOut"
                      ? optOut.expiresAt
                      : null
                  }
                  deletedAt={optOut.deletedAt}
                  note={note}
                />
              );
            })}
          </NoBulletList>
        ),
      };
    } else {
      return null;
    }
  }, [optOutDetails, userHasTenantWideAccess]);

  if (optOutDetailsLoading) {
    return <Loading />;
  }

  if (optOutDetailsError) {
    return <ErrorCard errors={[optOutDetailsError]} />;
  }

  return (
    <>
      <AccentBox sx={{ gap: theme.spacing(2) }}>
        <Typography variant="h1">
          <FormattedMessage
            defaultMessage="Opt Out Details for {phoneNumber}"
            description="Title for opted out number details"
            values={{
              phoneNumber: <strong>{prettyPhoneNumber(phoneNumber)}</strong>,
            }}
          />
        </Typography>
        {optOutDetails?.isTextable ? (
          <FormattedMessage
            defaultMessage="This number is currently textable."
            description="Label for a phone number that is textable"
          />
        ) : null}
        <TwoColumnDisplay
          allRowSx={{ my: 1, alignItems: "top" }}
          rows={[
            globalOptOutListRow,
            subaccountOptOutListRow,
            globalStrikeListRow,
            subaccountStrikeListRow,
            autoOptOutRow,
            shortCodeOptOutRow,
            texterOptOutRow,
            externalOptOutRow,
          ].filter((row): row is TwoColumnDisplayRow => !!row)}
        />
      </AccentBox>
      {hasLegallyRequiredOptOuts ? (
        <Alert
          severity="warning"
          icon={false}
          sx={{ marginTop: theme.spacing(2) }}
        >
          <FormattedMessage
            defaultMessage="{warningLabel} This number was automatically opted out because of an Opt Out keyword. Reversing this Opt Out could have legal repercussions."
            description="Warning for opting in a number that was opted out because of a keyword"
            values={{
              warningLabel: (
                <strong>
                  <FormattedMessage
                    defaultMessage="Warning"
                    description="Label before a warning"
                  />
                </strong>
              ),
            }}
          />
        </Alert>
      ) : null}
    </>
  );
}

function OptOutItem({
  id,
  title,
  subaccount,
  creationLabel,
  expiresAt,
  deletedAt,
  note,
}: {
  id: string;
  title: ReactNode;
  subaccount?: { id: string; name: string; slug: string } | null;
  creationLabel: ReactNode;
  expiresAt?: number | null;
  deletedAt?: number | null;
  note?: ReactNode;
}) {
  const subaccountContext = useSubaccount();

  const expired = !deletedAt && expiresAt && expiresAt < Date.now();

  function renderTitle() {
    if (deletedAt) {
      return (
        <FormattedMessage
          defaultMessage="{title} (Reversed)"
          description="Title for a reversed Opt Out"
          values={{ title }}
        />
      );
    } else if (expired) {
      return (
        <FormattedMessage
          defaultMessage="{title} (Expired)"
          description="Title for an expired Opt Out"
          values={{ title }}
        />
      );
    } else {
      return title;
    }
  }

  return (
    <li
      key={id}
      style={
        expired || deletedAt
          ? { fontStyle: "italic", color: palette.darkGray }
          : {}
      }
    >
      <Typography>{renderTitle()}</Typography>
      {subaccount && subaccount.id !== subaccountContext?.subaccount.id && (
        <Typography variant="body3" component="p">
          <FormattedMessage
            defaultMessage="Subaccount: {subaccountLink}"
            description="Link to an Opt Out's subaccount"
            values={{
              subaccountLink: (
                <InternalLink to={`/subaccounts/${subaccount.slug}/opt-outs`}>
                  {subaccount.name}
                </InternalLink>
              ),
            }}
          />
        </Typography>
      )}
      <Typography variant="body3" component="p">
        {creationLabel}
      </Typography>
      {deletedAt && (
        <Typography variant="body3" component="p">
          <FormattedMessage
            defaultMessage="Removed on {deletedAt}"
            description="Opt out deletion label"
            values={{ deletedAt: <FormattedDateTime value={deletedAt} /> }}
          />
        </Typography>
      )}
      {expiresAt && (
        <Typography variant="body3" component="p">
          {expired ? (
            <FormattedMessage
              defaultMessage="Expired on {expiresAt}"
              description="Expiration label for an Opt Out that has already expired"
              values={{ expiresAt: <FormattedDateTime value={expiresAt} /> }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="Expires on {expiresAt}"
              description="Expiration label for an Opt Out that will expire in the future"
              values={{ expiresAt: <FormattedDateTime value={expiresAt} /> }}
            />
          )}
        </Typography>
      )}
      {note && (
        <Typography variant="body3" component="p">
          {note}
        </Typography>
      )}
    </li>
  );
}

function ListOptOutCreationLabel({
  createdBy,
  createdAt,
}: {
  createdBy?: string | null;
  createdAt: number;
}): ReactElement {
  if (createdBy) {
    return (
      <FormattedMessage
        defaultMessage="Added by {createdBy} on {createdAt}"
        description="Label for creation date for a list Opt Out with known creator"
        values={{
          createdAt: <FormattedDateTime value={createdAt} />,
          createdBy,
        }}
      />
    );
  } else {
    return (
      <FormattedMessage
        defaultMessage="Added on {createdAt}"
        description="Label for creation date for a list Opt Out with unknown creator"
        values={{
          createdAt: <FormattedDateTime value={createdAt} />,
        }}
      />
    );
  }
}
